'use client';
import { useState } from 'react';

/* ─── Status config ───────────────────────────────────────────────────────── */
const STATUS_CFG = {
    OPEN:        { label: 'Open',        color: '#6366f1', bg: 'rgba(99,102,241,0.10)',  border: 'rgba(99,102,241,0.25)',  icon: 'pi pi-circle',           dot: '#6366f1' },
    IN_PROGRESS: { label: 'In Progress', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.25)',  icon: 'pi pi-spin pi-spinner',  dot: '#f59e0b' },
    RESOLVED:    { label: 'Resolved',    color: '#10b981', bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.25)',  icon: 'pi pi-check-circle',     dot: '#10b981' },
    CLOSED:      { label: 'Closed',      color: '#64748b', bg: 'rgba(100,116,139,0.10)',border: 'rgba(100,116,139,0.25)', icon: 'pi pi-times-circle',     dot: '#64748b' },
};

const PRIORITY_CFG = {
    URGENT: { label: 'Urgent', color: '#ef4444', bg: 'rgba(239,68,68,0.10)'  },
    HIGH:   { label: 'High',   color: '#f97316', bg: 'rgba(249,115,22,0.10)' },
    MEDIUM: { label: 'Medium', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
    LOW:    { label: 'Low',    color: '#10b981', bg: 'rgba(16,185,129,0.10)' },
};

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const fmt = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtRelative = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60)  return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
};

/* ─── Timeline step ───────────────────────────────────────────────────────── */
const TimelineStep = ({ label, date, active, done, color }) => (
    <div className="flex align-items-center gap-2">
        <div style={{
            width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
            background: done || active ? color : '#e2e8f0',
            border: `2px solid ${done || active ? color : '#cbd5e1'}`,
            boxShadow: active ? `0 0 0 3px ${color}30` : 'none',
        }} />
        <div>
            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: done || active ? color : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label}
            </span>
            {date && (
                <span style={{ fontSize: '0.62rem', color: '#94a3b8', marginLeft: '0.4rem' }}>
                    {fmt(date)}
                </span>
            )}
        </div>
    </div>
);

/* ─── Reply preview ───────────────────────────────────────────────────────── */
const ReplyPreview = ({ reply }) => (
    <div
        className="flex align-items-start gap-2 border-round-lg p-3 mt-2"
        style={{
            background: reply.isAdmin ? 'rgba(99,102,241,0.06)' : '#f8faff',
            border: `1px solid ${reply.isAdmin ? 'rgba(99,102,241,0.18)' : 'rgba(11,49,99,0.1)'}`,
        }}
    >
        <div
            className="flex align-items-center justify-content-center border-circle flex-shrink-0"
            style={{
                width: 28, height: 28,
                background: reply.isAdmin ? 'rgba(99,102,241,0.15)' : 'rgba(11,49,99,0.08)',
            }}
        >
            <i className={reply.isAdmin ? 'pi pi-headphones' : 'pi pi-user'} style={{ fontSize: '0.62rem', color: reply.isAdmin ? '#6366f1' : '#0b3163' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div className="flex align-items-center gap-2 mb-1">
                <span style={{ fontSize: '0.68rem', fontWeight: 700, color: reply.isAdmin ? '#6366f1' : '#0b3163' }}>
                    {reply.isAdmin ? (reply.repliedBy || 'Support Team') : 'You'}
                </span>
                <span style={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                    {fmtRelative(reply.createdAt)}
                </span>
            </div>
            <p className="m-0" style={{ fontSize: '0.78rem', color: '#475569', lineHeight: 1.6, wordBreak: 'break-word' }}>
                {reply.message}
            </p>
        </div>
    </div>
);

/* ─── Main card ───────────────────────────────────────────────────────────── */
export const TicketStatusCard = ({ ticket }) => {
    const [showReplies, setShowReplies] = useState(false);

    const status   = STATUS_CFG[ticket.status]   ?? STATUS_CFG.OPEN;
    const priority = PRIORITY_CFG[ticket.priority] ?? PRIORITY_CFG.MEDIUM;

    const steps = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];
    const currentIdx = steps.indexOf(ticket.status);

    const latestReply = ticket.replies?.length
        ? ticket.replies[ticket.replies.length - 1]
        : null;

    return (
        <div
            style={{
                borderRadius:  '1.1rem',
                border:        `2px solid ${status.border}`,
                background:    '#ffffff',
                overflow:      'hidden',
                boxShadow:     `0 4px 20px rgba(0,0,0,0.08)`,
            }}
        >
            {/* ── Header ────────────────────────────────────────────────── */}
            <div
                style={{
                    background: `linear-gradient(135deg, ${status.color}14, ${status.color}06)`,
                    borderBottom: `1px solid ${status.border}`,
                    padding: '1rem 1.25rem',
                }}
            >
                <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                    {/* Left: number + queryType */}
                    <div className="flex align-items-center gap-3">
                        <div
                            className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                            style={{
                                width: 40, height: 40,
                                background: `${status.color}15`,
                                border: `1.5px solid ${status.border}`,
                            }}
                        >
                            <i className="pi pi-ticket" style={{ fontSize: '0.9rem', color: status.color }} />
                        </div>
                        <div>
                            <p className="m-0 font-bold" style={{ fontSize: '0.92rem', color: '#0b3163', letterSpacing: '0.02em' }}>
                                {ticket.ticketNumber}
                            </p>
                            <p className="m-0 mt-1" style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                {ticket.queryType}
                                {ticket.category ? ` · ${ticket.category}` : ''}
                            </p>
                        </div>
                    </div>

                    {/* Right: status + priority badges */}
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <span
                            className="flex align-items-center gap-1 font-bold border-round-3xl"
                            style={{
                                background: status.bg,
                                color:      status.color,
                                border:     `1.5px solid ${status.border}`,
                                fontSize:   '0.7rem',
                                padding:    '0.25rem 0.65rem',
                            }}
                        >
                            <i className={status.icon} style={{ fontSize: '0.62rem' }} />
                            {status.label}
                        </span>
                        <span
                            style={{
                                background: priority.bg,
                                color:      priority.color,
                                border:     `1px solid ${priority.color}30`,
                                borderRadius: '999px',
                                fontSize:   '0.62rem',
                                fontWeight: 700,
                                padding:    '0.2rem 0.5rem',
                            }}
                        >
                            {priority.label}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Body ──────────────────────────────────────────────────── */}
            <div className="p-4">

                {/* Product info */}
                {ticket.product && (
                    <div
                        className="flex align-items-start gap-2 border-round-lg px-3 py-2 mb-3"
                        style={{ background: 'rgba(27,184,167,0.06)', border: '1px solid rgba(27,184,167,0.2)' }}
                    >
                        <i className="pi pi-box" style={{ fontSize: '0.78rem', color: '#1bb8a7', marginTop: '0.15rem', flexShrink: 0 }} />
                        <p className="m-0" style={{ fontSize: '0.78rem', color: '#334155', lineHeight: 1.5 }}>
                            {ticket.product}
                        </p>
                    </div>
                )}

                {/* Description */}
                {(ticket.description || ticket.message) && (
                    <div className="mb-3">
                        <p className="m-0 mb-1" style={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            Issue Description
                        </p>
                        <p className="m-0" style={{ fontSize: '0.82rem', color: '#334155', lineHeight: 1.65 }}>
                            {ticket.description || ticket.message}
                        </p>
                    </div>
                )}

                {/* Dates row */}
                <div className="flex gap-4 flex-wrap mb-4" style={{ borderTop: '1px solid #f1f5f9', paddingTop: '0.85rem', marginTop: '0.5rem' }}>
                    <div>
                        <p className="m-0" style={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Raised on</p>
                        <p className="m-0 mt-1 font-bold" style={{ fontSize: '0.8rem', color: '#334155' }}>{fmt(ticket.createdAt)}</p>
                    </div>
                    {ticket.maxResolveDate && (
                        <div>
                            <p className="m-0" style={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>SLA Deadline</p>
                            <p className="m-0 mt-1 font-bold" style={{ fontSize: '0.8rem', color: new Date(ticket.maxResolveDate) < new Date() && ticket.status !== 'RESOLVED' && ticket.status !== 'CLOSED' ? '#ef4444' : '#334155' }}>
                                {fmt(ticket.maxResolveDate)}
                            </p>
                        </div>
                    )}
                    {ticket.resolvedAt && (
                        <div>
                            <p className="m-0" style={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Resolved on</p>
                            <p className="m-0 mt-1 font-bold" style={{ fontSize: '0.8rem', color: '#10b981' }}>{fmt(ticket.resolvedAt)}</p>
                        </div>
                    )}
                    {ticket.assignedTo && (
                        <div>
                            <p className="m-0" style={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Assigned to</p>
                            <p className="m-0 mt-1 font-bold" style={{ fontSize: '0.8rem', color: '#334155' }}>{ticket.assignedTo}</p>
                        </div>
                    )}
                </div>

                {/* Progress timeline */}
                <div
                    className="flex align-items-center gap-0 mb-4"
                    style={{ borderRadius: '0.65rem', background: '#f8faff', padding: '0.75rem 1rem', border: '1px solid #e2e8f0' }}
                >
                    {steps.map((step, i) => {
                        const done   = i < currentIdx;
                        const active = i === currentIdx;
                        const cfg    = STATUS_CFG[step];
                        return (
                            <div key={step} className="flex align-items-center" style={{ flex: i < steps.length - 1 ? 1 : 'none' }}>
                                <div className="flex flex-column align-items-center gap-1">
                                    <div style={{
                                        width: 12, height: 12, borderRadius: '50%',
                                        background: done || active ? cfg.color : '#e2e8f0',
                                        border: `2px solid ${done || active ? cfg.color : '#cbd5e1'}`,
                                        boxShadow: active ? `0 0 0 4px ${cfg.color}25` : 'none',
                                        transition: 'all 0.3s',
                                    }} />
                                    <span style={{ fontSize: '0.58rem', fontWeight: 700, color: done || active ? cfg.color : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                                        {cfg.label}
                                    </span>
                                </div>
                                {i < steps.length - 1 && (
                                    <div style={{
                                        flex: 1, height: 2, margin: '0 0.35rem',
                                        background: done ? STATUS_CFG[steps[i + 1]].color : '#e2e8f0',
                                        marginBottom: '1rem',
                                        transition: 'background 0.3s',
                                    }} />
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Latest reply */}
                {latestReply && (
                    <div>
                        <div className="flex align-items-center justify-content-between mb-1">
                            <p className="m-0" style={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                Latest Update
                            </p>
                            {ticket.replies.length > 1 && (
                                <button
                                    onClick={() => setShowReplies(v => !v)}
                                    style={{
                                        background: 'transparent', border: 'none', cursor: 'pointer',
                                        fontSize: '0.68rem', color: status.color, fontWeight: 700,
                                        display: 'flex', alignItems: 'center', gap: '0.3rem',
                                    }}
                                >
                                    {showReplies ? 'Hide' : `Show all ${ticket.replies.length}`}
                                    <i className={`pi pi-chevron-${showReplies ? 'up' : 'down'}`} style={{ fontSize: '0.6rem' }} />
                                </button>
                            )}
                        </div>
                        {showReplies
                            ? ticket.replies.map((r, i) => <ReplyPreview key={i} reply={r} />)
                            : <ReplyPreview reply={latestReply} />
                        }
                    </div>
                )}

                {/* Awaiting info */}
                {ticket.awaitingReplyFrom && (
                    <div
                        className="flex align-items-center gap-2 border-round-lg px-3 py-2 mt-3"
                        style={{
                            background: ticket.awaitingReplyFrom === 'USER' ? 'rgba(245,158,11,0.08)' : 'rgba(99,102,241,0.08)',
                            border:     ticket.awaitingReplyFrom === 'USER' ? '1px solid rgba(245,158,11,0.2)' : '1px solid rgba(99,102,241,0.2)',
                        }}
                    >
                        <i className="pi pi-clock" style={{ fontSize: '0.75rem', color: ticket.awaitingReplyFrom === 'USER' ? '#f59e0b' : '#6366f1' }} />
                        <p className="m-0" style={{ fontSize: '0.75rem', color: ticket.awaitingReplyFrom === 'USER' ? '#f59e0b' : '#6366f1' }}>
                            {ticket.awaitingReplyFrom === 'USER'
                                ? 'Awaiting your reply — please contact our support team to provide more details.'
                                : 'Our support team is reviewing your request and will reply soon.'}
                        </p>
                    </div>
                )}

            </div>
        </div>
    );
};
